<template>
  <div v-if="visible" class="device-menu">
    <div class="device-header">
      <h3>Настройки устройств и качества</h3>
      <button @click="emit('close')" class="close-btn" title="Закрыть меню настроек">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Качество видео -->
    <div v-if="hasVideo" class="device-section">
      <h4><i class="fas fa-video"></i> Качество видео</h4>
      <div class="quality-dropdown">
        <select
          :value="currentQuality"
          @change="onChangeQuality"
          class="quality-select"
        >
          <option
            v-for="(profile, key) in qualityProfiles"
            :key="key"
            :value="key"
            :disabled="!isQualitySupported(key)"
          >
            {{ key }} - {{ profile.width }}x{{ profile.height }} @ {{ profile.frameRate }}fps
            {{ !isQualitySupported(key) ? '(не поддерживается)' : '' }}
          </option>
        </select>
      </div>
      <div class="quality-info">
        <span class="quality-hint">
          <i class="fas fa-lightbulb"></i> Текущее: {{ currentQuality }}
        </span>
        <div v-if="deviceCapabilities.isLowEndDevice" class="device-warning">
          <i class="fas fa-exclamation-triangle"></i> Слабое устройство - ограниченные возможности качества
        </div>
        <div v-if="deviceCapabilities.isMobile" class="device-info">
          <i class="fas fa-mobile-alt"></i> Мобильное устройство
        </div>
      </div>
    </div>

    <!-- Микрофон -->
    <div class="device-section">
      <h4><i class="fas fa-microphone"></i> Микрофон</h4>
      <div class="device-dropdown">
        <select
          :value="audioDeviceInfo.currentInputDevice?.deviceId || ''"
          @change="emit('select-audio-input', ($event.target as HTMLSelectElement).value)"
          class="device-select"
        >
          <option
            v-for="device in audioDeviceInfo.availableInputDevices"
            :key="device.deviceId"
            :value="device.deviceId"
          >
            {{ device.label }}
          </option>
        </select>
      </div>
    </div>

    <!-- Динамики -->
    <div class="device-section">
      <h4><i class="fas fa-volume-up"></i> Динамики</h4>
      <div class="device-dropdown">
        <select
          :value="audioDeviceInfo.currentOutputDevice?.deviceId || ''"
          @change="emit('select-audio-output', ($event.target as HTMLSelectElement).value)"
          class="device-select"
        >
          <option
            v-for="device in audioDeviceInfo.availableOutputDevices"
            :key="device.deviceId"
            :value="device.deviceId"
          >
            {{ device.label }}
          </option>
        </select>
      </div>
    </div>

    <!-- Камера -->
    <div v-if="cameraInfo.availableCameras.length > 1" class="device-section">
      <h4><i class="fas fa-camera"></i> Камера</h4>
      <div class="device-dropdown">
        <select
          :value="cameraInfo.currentCamera?.deviceId || ''"
          @change="emit('select-camera', ($event.target as HTMLSelectElement).value)"
          class="device-select"
        >
          <option
            v-for="camera in cameraInfo.availableCameras"
            :key="camera.deviceId"
            :value="camera.deviceId"
          >
            {{ camera.label }} ({{ formatCameraType(camera.type) }})
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Компонент меню выбора устройств и качества
 * Позволяет выбирать камеру, микрофон, динамики и качество видео
 */

import type {
  QualityLevel,
  QualityProfile,
  CameraState,
  AudioDeviceState,
  DeviceCapabilities
} from '../../types';
import { formatCameraType } from '../../utils/connectionState';

// ==================== PROPS ====================

interface Props {
  /** Видимость меню */
  visible: boolean;
  /** Флаг наличия видео */
  hasVideo: boolean;
  /** Текущее качество */
  currentQuality: QualityLevel;
  /** Профили качества */
  qualityProfiles: Record<QualityLevel, QualityProfile>;
  /** Возможности устройства */
  deviceCapabilities: DeviceCapabilities;
  /** Информация о камерах */
  cameraInfo: CameraState;
  /** Информация об аудио устройствах */
  audioDeviceInfo: AudioDeviceState;
  /** Функция проверки поддержки качества */
  isQualitySupported: (quality: QualityLevel) => boolean;
}

const props = defineProps<Props>();

// ==================== EMITS ====================

const emit = defineEmits<{
  'close': [];
  'change-quality': [quality: QualityLevel];
  'select-audio-input': [deviceId: string];
  'select-audio-output': [deviceId: string];
  'select-camera': [deviceId: string];
}>();


const onChangeQuality = (event: Event) => {
  const select = (event.target as HTMLSelectElement).value;

  emit('change-quality', select as QualityLevel);
};

</script>

<style scoped>
.device-menu {
  position: fixed;
  bottom: 120px;
  left: 50%;
  transform: translateX(-50%);
  width: 400px;
  padding: 24px;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 100;
  animation: slideUp 0.3s ease;
  max-height: 70vh;
  overflow-y: auto;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.device-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.device-header h3 {
  margin: 0;
  font-size: 1rem;
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  transform: scale(1.05);
}

.device-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.device-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.device-section h4 {
  margin: 0 0 0.75rem 0;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Dropdown стили */
.device-dropdown,
.quality-dropdown {
  margin-bottom: 0.75rem;
}

.device-select,
.quality-select {
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 16px;
  padding-right: 40px;
}

.device-select:hover,
.quality-select:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(59, 130, 246, 0.5);
}

.device-select:focus,
.quality-select:focus {
  outline: none;
  border-color: rgba(59, 130, 246, 0.8);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.device-select option,
.quality-select option {
  background: #1a1a1a;
  color: #fff;
  padding: 8px;
}

.device-select option:disabled,
.quality-select option:disabled {
  color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.05);
}

.quality-info {
  margin-top: 0.5rem;
}

.quality-hint {
  display: block;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
}

.device-warning,
.device-info {
  font-size: 0.75rem;
  padding: 0.5rem;
  border-radius: 6px;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.device-warning {
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  color: #fbbf24;
}

.device-info {
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #3b82f6;
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .device-menu {
    width: calc(100% - 20px);
    left: 10px;
    right: 10px;
    transform: none;
    max-height: calc(100dvh - 140px);
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>
